import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, Wallet, Banknote, Landmark, Gem, Package, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";

// Assets & Liabilities dashboard. Server keeps everything in db.user_assets and
// returns per-user rows via /api/assets. Summary card up top shows totals per
// category + a running net worth. Any user can list any type; article covers
// antiques / gadgets / jewellery — the free-form "valuation_inr" is self-reported.

const CATS = [
  { key: "cash",      label: "Cash",       icon: Banknote,  color: "text-emerald-700 bg-emerald-50" },
  { key: "bank",      label: "Bank",       icon: Landmark,  color: "text-sky-700 bg-sky-50" },
  { key: "gold",      label: "Gold",       icon: Gem,       color: "text-amber-700 bg-amber-50" },
  { key: "silver",    label: "Silver",     icon: Gem,       color: "text-stone-700 bg-stone-100" },
  { key: "share",     label: "Shares",     icon: TrendingUp,color: "text-purple-700 bg-purple-50" },
  { key: "article",   label: "Articles",   icon: Package,   color: "text-orange-700 bg-orange-50" },
  { key: "liability", label: "Liabilities",icon: TrendingDown,color: "text-red-700 bg-red-50" },
];

const CAT_META = Object.fromEntries(CATS.map(c => [c.key, c]));

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Assets() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useSeo({ title: "My Assets & Liabilities", description: "Track cash, bank balances, gold, silver, shares, articles and liabilities in one balance sheet.", canonical: "/assets" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/assets");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Could not load assets");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user?.user_id]);

  const summary = useMemo(() => {
    const totals = { cash: 0, bank: 0, gold: 0, silver: 0, share: 0, article: 0, liability: 0 };
    items.forEach(i => { totals[i.category] = (totals[i.category] || 0) + Number(i.valuation_inr || 0); });
    const assets_sum = Object.entries(totals).filter(([k]) => k !== "liability").reduce((s, [, v]) => s + v, 0);
    return { totals, assets_sum, liabilities: totals.liability, net_worth: assets_sum - totals.liability };
  }, [items]);

  const filtered = tab === "all" ? items : items.filter(i => i.category === tab);

  const removeItem = async (id) => {
    if (!window.confirm("Remove this row?")) return;
    try { await api.delete(`/assets/${id}`); setItems(x => x.filter(i => i.asset_id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (!user) return (
    <div className="max-w-md mx-auto px-5 py-20 text-center text-stone-500">
      Please login to track your assets.
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8" data-testid="assets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Personal balance sheet</div>
          <h1 className="heading text-2xl md:text-4xl font-extrabold text-stone-900">Assets & Liabilities</h1>
        </div>
        <Button onClick={() => setShowForm(v => !v)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] min-h-[44px]" data-testid="assets-add-btn">
          <Plus size={14} className="mr-1" /> {showForm ? "Cancel" : "Add row"}
        </Button>
      </div>

      {/* Summary card */}
      <div className="rounded-3xl bg-gradient-to-br from-stone-900 to-stone-800 text-white p-5 md:p-6" data-testid="assets-summary">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Assets total</div>
            <div className="heading text-2xl md:text-3xl font-extrabold mt-1" data-testid="assets-total">{inr(summary.assets_sum)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Liabilities</div>
            <div className="heading text-2xl md:text-3xl font-extrabold mt-1 text-red-300" data-testid="assets-liabilities">{inr(summary.liabilities)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Net worth</div>
            <div className="heading text-2xl md:text-3xl font-extrabold mt-1 text-emerald-300" data-testid="assets-networth">{inr(summary.net_worth)}</div>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="assets-tabs">
        <TabPill k="all" label="All" n={items.length} active={tab === "all"} onClick={setTab} />
        {CATS.map(c => (
          <TabPill
            key={c.key}
            k={c.key}
            label={c.label}
            n={items.filter(i => i.category === c.key).length}
            active={tab === c.key}
            onClick={setTab}
          />
        ))}
      </div>

      {/* Add form */}
      {showForm && <AddForm onDone={(row) => { setItems(x => [row, ...x]); setShowForm(false); }} />}

      {/* Item grid */}
      {loading ? <Loader2 className="mx-auto mt-12 animate-spin text-stone-400" /> :
        filtered.length === 0 ? (
          <div className="mt-8 text-center text-stone-500 text-sm" data-testid="assets-empty">
            No {tab === "all" ? "" : CAT_META[tab]?.label + " "}entries yet. Tap <b>Add row</b> above to record your first one.
          </div>
        ) : (
          <div className="mt-6 grid gap-3 md:grid-cols-2" data-testid="assets-list">
            {filtered.map(i => {
              const meta = CAT_META[i.category] || CAT_META.article;
              const Icon = meta.icon;
              return (
                <div key={i.asset_id} className="bg-white border border-stone-200 rounded-2xl p-4 flex gap-3" data-testid={`asset-row-${i.asset_id}`}>
                  {i.photo_url ? (
                    <img loading="lazy" src={i.photo_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                  ) : (
                    <div className={`w-16 h-16 rounded-xl grid place-items-center shrink-0 ${meta.color}`}><Icon size={22} /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                      {i.held_as_metal && <span className="text-[9px] uppercase tracking-widest font-black text-amber-700">Held as metal</span>}
                    </div>
                    <div className="font-bold text-stone-900 truncate">{i.label}</div>
                    <div className="text-xs text-stone-500 space-x-1">
                      {i.quantity && <span>{i.quantity}{i.unit ? ` ${i.unit}` : ""}</span>}
                      {i.weight_grams > 0 && <span>· {i.weight_grams}g</span>}
                      {i.ticker && <span>· {i.ticker}</span>}
                    </div>
                    {i.description && <div className="text-xs text-stone-500 line-clamp-1 mt-0.5">{i.description}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`heading font-black ${i.category === "liability" ? "text-red-600" : "text-[#2D5A27]"}`}>
                      {i.category === "liability" ? "−" : ""}{inr(i.valuation_inr)}
                    </div>
                    <button
                      onClick={() => removeItem(i.asset_id)}
                      className="mt-2 text-[11px] text-stone-500 hover:text-red-600 inline-flex items-center gap-1 min-h-[36px]"
                      data-testid={`asset-delete-${i.asset_id}`}
                    >
                      <Trash2 size={11} /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

function TabPill({ k, label, n, active, onClick }) {
  return (
    <button
      onClick={() => onClick(k)}
      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full min-h-[36px] ${
        active ? "bg-[#E05E36] text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"
      }`}
      data-testid={`assets-tab-${k}`}
    >
      {label} {n > 0 && <span className={`text-[10px] font-mono px-1.5 rounded-full ${active ? "bg-white/25" : "bg-stone-100"}`}>{n}</span>}
    </button>
  );
}

function AddForm({ onDone }) {
  const [form, setForm] = useState({ category: "cash", label: "", valuation_inr: "", quantity: "", unit: "", weight_grams: "", ticker: "", description: "", photo_url: "", held_as_metal: false });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.label.trim() || !form.valuation_inr) { toast.error("Label + valuation required"); return; }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        label: form.label.trim(),
        valuation_inr: Number(form.valuation_inr),
        quantity: form.quantity ? Number(form.quantity) : null,
        unit: form.unit || null,
        weight_grams: form.weight_grams ? Number(form.weight_grams) : null,
        ticker: form.ticker || null,
        description: form.description || null,
        photo_url: form.photo_url || null,
        held_as_metal: !!form.held_as_metal,
      };
      const r = await api.post("/assets", payload);
      toast.success("Added");
      onDone?.(r.data.asset);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const isMetal = form.category === "gold" || form.category === "silver";
  const isShare = form.category === "share";
  return (
    <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="assets-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Category</label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger className="mt-1" data-testid="assets-form-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATS.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Label</label>
          <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. HDFC Savings" className="mt-1" data-testid="assets-form-label" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Valuation (₹)</label>
          <Input type="number" value={form.valuation_inr} onChange={(e) => set("valuation_inr", e.target.value)} className="mt-1" data-testid="assets-form-value" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Photo URL (optional)</label>
          <Input value={form.photo_url} onChange={(e) => set("photo_url", e.target.value)} className="mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Qty</label>
            <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Unit</label>
            <Input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="g / pcs / shares" className="mt-1" />
          </div>
        </div>
        {isMetal && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Weight (g)</label>
              <Input type="number" value={form.weight_grams} onChange={(e) => set("weight_grams", e.target.value)} className="mt-1" />
            </div>
            <label className="flex items-center gap-2 mt-6 text-xs text-stone-700">
              <input
                type="checkbox"
                checked={form.held_as_metal}
                onChange={(e) => set("held_as_metal", e.target.checked)}
                className="w-4 h-4"
                data-testid="assets-form-heldasmetal"
              />
              Held as metal
            </label>
          </div>
        )}
        {isShare && (
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Ticker</label>
            <Input value={form.ticker} onChange={(e) => set("ticker", e.target.value)} placeholder="TCS.NS" className="mt-1" />
          </div>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Description (optional)</label>
        <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Age / rarity / mint / anything worth noting" className="mt-1 min-h-[70px]" />
      </div>
      <Button onClick={save} disabled={saving} className="w-full rounded-full bg-[#2D5A27] hover:bg-[#1e3f1a] min-h-[44px]" data-testid="assets-form-save">
        {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving…</> : "Save"}
      </Button>
    </div>
  );
}
